import React, { useState, useEffect, useCallback } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const defaultScreenshots = [
  { src: '/screenshots/inventory.png', caption: 'Inventory Management' },
  { src: '/screenshots/inventory-archive.png', caption: 'Inventory Archive' },
  { src: '/screenshots/channels.png', caption: 'Sales Channel Management' },
  { src: '/screenshots/expense.png', caption: 'Expense Tracking' },
  { src: '/screenshots/mileage.png', caption: 'Mileage Tracking' },
  { src: '/screenshots/sales.png', caption: 'Sales Transactions' },
  { src: '/screenshots/reports.png', caption: 'Reports & Analytics' },
  { src: '/screenshots/ai-agent.png', caption: 'AI Assistant' },
  { src: '/screenshots/account.png', caption: 'Account Management' },
];

const ScreenshotCarousel = ({ screenshots = defaultScreenshots, interval = 5000 }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const goToNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % screenshots.length);
  }, [screenshots.length]);

  const goToPrev = () => {
    setActiveIndex((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  };

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(goToNext, interval);
    return () => clearInterval(timer);
  }, [isPaused, interval, goToNext]);

  return (
    <Box
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      sx={{ position: 'relative', maxWidth: 900, mx: 'auto' }}
    >
      {/* Slide area */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          overflow: 'hidden',
          borderRadius: 2,
          boxShadow: 3,
          bgcolor: 'grey.200',
        }}
      >
        {screenshots.map((shot, idx) => (
          <Box
            key={idx}
            component="img"
            src={shot.src}
            alt={shot.caption}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: idx === activeIndex ? 1 : 0,
              transition: 'opacity 0.6s ease-in-out',
            }}
          />
        ))}

        {/* Prev / Next arrows */}
        <IconButton
          onClick={goToPrev}
          sx={{
            position: 'absolute',
            top: '50%',
            left: 8,
            transform: 'translateY(-50%)',
            bgcolor: 'rgba(0,0,0,0.4)',
            color: 'common.white',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          onClick={goToNext}
          sx={{
            position: 'absolute',
            top: '50%',
            right: 8,
            transform: 'translateY(-50%)',
            bgcolor: 'rgba(0,0,0,0.4)',
            color: 'common.white',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Caption */}
      <Typography
        variant="body1"
        sx={{ textAlign: 'center', mt: 2, fontWeight: 500, color: 'text.secondary' }}
      >
        {screenshots[activeIndex].caption}
      </Typography>

      {/* Dot indicators */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1.5 }}>
        {screenshots.map((_, idx) => (
          <Box
            key={idx}
            onClick={() => setActiveIndex(idx)}
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: idx === activeIndex ? 'primary.main' : 'grey.400',
              cursor: 'pointer',
              transition: 'background-color 0.3s',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ScreenshotCarousel;
